import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Order, QueryConfig, QueryEntity } from '@datorama/akita';
import { first } from 'lodash-es';
import { map } from 'rxjs/operators';

import { listSitesQuery } from '@shared/services/gql.service';
import { Utils } from '@shared/utils/utils';

import { SiteModel, SitesState, SitesStore } from './sites.store';
import { SiteOption } from '../site-option.model';

function mapSiteToSiteOption(
  site: listSitesQuery | SiteModel,
  principalInvestigatorName: string
): SiteOption {
  return {
    siteName: site.name || '',
    subTitle: principalInvestigatorName
      ? `${site.name} - ${principalInvestigatorName}`
      : site.name || '',
    textToSearch: `${site.site_no} ${site.name} ${principalInvestigatorName}`,
    title: site.site_no || '',
    value: site.id,
  };
}

@Injectable({ providedIn: 'root' })
@QueryConfig({
  sortBy: 'name',
  sortByOrder: Order.ASC,
})
export class SitesQuery extends QueryEntity<SitesState> {
  readonly sites = toSignal(this.selectAll(), { requireSync: true });

  protected store: SitesStore;

  constructor() {
    const store = inject(SitesStore);

    super(store);

    this.store = store;
  }

  private getPrincipalInvestigatorName(siteId: string): string {
    const principalInvestigator = first(this.getEntity(siteId)?.contacts);

    return principalInvestigator
      ? `${principalInvestigator.given_name} ${principalInvestigator.family_name}`.trim()
      : '';
  }

  getSortedListBy(sortBy: keyof SiteModel) {
    return this.getAll({
      sortBy,
      sortByOrder: Order.ASC,
    });
  }

  selectSiteOptions() {
    return computed(() => {
      return this.sites()
        .sort(({ site_no }, { site_no: site_no2 }) =>
          Utils.localeAlphaNumSort(site_no || '', site_no2 || '')
        )
        .map((site: SiteModel) =>
          mapSiteToSiteOption(site, this.getPrincipalInvestigatorName(site.id))
        );
    });
  }

  selectSortedListBy() {
    return this.select().pipe(
      map((sites) => {
        const entities = sites.entities;

        if (entities) {
          const siteArray = Object.values(entities);

          return siteArray.sort(({ site_no }, { site_no: site_no2 }) =>
            Utils.localeAlphaNumSort(site_no || '', site_no2 || '')
          ) as listSitesQuery[];
        }

        return [];
      })
    );
  }
}
